import EventManager from "../../components/EventManager"
import deepForEach from "../deepForEach";
import {AddRemoveData, SupportsEventListenerManager} from "../../components/EventListenerManager";
import {deepCopyObject} from "../deepCopy";
import deepFreeze from "../deepFreeze";

/**
 * See class description for details of each event.
 */
export interface JsfAjaxEventMap {
	"begin":		JsfAjaxRequestEvent;
	"responded":	JsfAjaxResponseEvent;
	"success":		JsfAjaxResponseEvent;
	"error":		JsfAjaxErrorEvent;
	"always":       JsfAjaxAlwaysEvent;
}
export interface JsfAjaxAddOptionsMap {
	"begin":		{sourceElement?: HTMLElement};
	"responded":	{sourceElement?: HTMLElement, updateId: string | string[]};
	"success":		{sourceElement?: HTMLElement, updateId: string | string[]};
	"error":		undefined;
	"always":		{sourceElement?: HTMLElement, updateId: string | string[]};
}

export interface JsfAjaxEvent {
	eventType: string;
	// A readonly copy of the request options so listeners can make decisions based on the data
	// Only here for "native" jsf events
	requestOptionsReadonlyCopy?: NativeJsfAjaxRequestOptions;
	// For internal communication between listeners and also request intercepts.
	internalOptions: any;
}
export interface JsfAjaxRequestEvent extends JsfAjaxEvent {
	eventType: "begin";
	sourceElement: HTMLElement;
}
export interface JsfAjaxResponseEvent extends JsfAjaxEvent {
	eventType: "responded" | "success";
	sourceElement: HTMLElement;
	responseCode: number;
	responseText: string;
	responseXML?: Document | null;
}
export interface JsfAjaxErrorEvent extends JsfAjaxEvent {
	eventType: "error";
	message?: string;
}
export interface JsfAjaxAlwaysEvent extends JsfAjaxEvent {
	eventType: "always";
	message?: string;
	sourceElement?: HTMLElement;
	responseCode?: number;
	responseText?: string;
	responseXML?: Document | null;
}
export interface JsfAjaxAlwaysAfterSuccessEvent extends JsfAjaxAlwaysEvent {
	sourceElement: HTMLElement;
	responseCode: number;
	responseText: string;
}
export interface JsfAjaxAlwaysAfterErrorEvent extends JsfAjaxAlwaysEvent {
	message: string;
}

type ListenerFunction = (
	sourceElement: Element, event: Event, requestOptions: NativeJsfAjaxRequestOptions, internalOptions: any
) => void;

const convertSourceToElement = (container: Document, source: string | JQuery | HTMLElement | undefined): HTMLElement | undefined => {
	if (source === undefined || source instanceof HTMLElement) {
		return source;
	} else if (typeof source === "string") {
		return container.getElementById(source) ?? undefined;
	} else {
		return source.get(0);
	}
}

/**
 * This class provides:
 * 1) addNativeJsfAjaxRequestListener() and removeNativeJsfAjaxRequestListener() - A way to intercept jsf.ajax.requests() calls. See addNativeJsfAjaxRequestListener() for details. Note: This does not include PrimeFaces' AJAX events.
 * 2) addEventListener() and removeEventListener() - Listen for AJAX events. This combines jsf.ajax and PrimeFaces ajax events into the following events:
 * 		begin - Request start
 * 		responded - Response received and before processed
 * 		success - Response processed
 * 		error - There was an error during the request or during processing
 * 	    always - Called if the the request was successful or errored. "Always" is fired after "success" and "error" would be fired.
 * 3) isNativeJsfAjaxLoaded(), getNativeJsfAjax(), getPrimeFacesAjax()
 *
 * Each request intercept and standard listeners are passed internalOptions for communicating internally between
 * listeners, and "native" JSF related standard listeners also have requestOptionsReadonlyCopy while the
 * request interceptor for "native" JSF ajax receive the writable, original request options.
 *
 * TODO Document the use of "options" argument on addEventListeners (Note: Must match all criteria if any options are provided; the updateId can be an array and only needs and only needs to match one).
 * TODO Document the use of "internalOptions". Maybe rename to "internalData".
 */
export default class JsfAjax implements SupportsEventListenerManager<JsfAjax> {
	private static readonly instances = new WeakMap<Window, JsfAjax>();

	private isInitAjaxRequestInterceptSetup = false;
	private onRequestListenersAll: Set<ListenerFunction> | undefined;
	private onRequestListenersForSpecificElements: WeakMap<Element, Set<ListenerFunction>> | undefined;

	private isInitPrimeFacesAjaxEventListening = false;

	private readonly eventManager = new EventManager<JsfAjaxEventMap, JsfAjaxAddOptionsMap>();
	public readonly addEventListener = this.eventManager.addEventListener;
	public readonly removeEventListener = this.eventManager.removeEventListener;

	public static getInstance(win: Window = window): JsfAjax {
		const instanceForWin = JsfAjax.instances.get(win);
		if (instanceForWin !== undefined) {
			return instanceForWin;
		} else {
			const urlUtil = new JsfAjax(win);
			JsfAjax.instances.set(win, urlUtil);
			return urlUtil;
		}
	}

	private constructor(private readonly win: Window) {
		this.eventManager.setOnAdd(
			() => {
				// Init listening/intercepts for events when the first addOnEventListener is called
				this.initNativeJsfAjaxRequestIntercept();
				this.initPrimeFacesAjaxEventListening();
				this.eventManager.removeOnAdd();
			}
		);
	}

	destroy() {
		JsfAjax.instances.delete(this.win);
		this.eventManager.destroy();
	}

	private filterEachListenerForNotify = (ev: any, listenerOptions?: any): boolean => {
		if (listenerOptions === undefined) {
			return true; // True means this listener should be executed / notified.
		}

		// Check the sourceElement if this listener's options has a specified sourceElement
		if (
			listenerOptions["sourceElement"] !== undefined
			&& ev.sourceElement !== listenerOptions["sourceElement"]
		) {
			return false;
		}

		// Check if one of the listener's update IDs is in the update.
		// updateId can be an array of strings or a single string. Only one needs to be found.
		// But if none are in the response, then return false.
		if (listenerOptions["updateId"] !== undefined) {
			const responseXML = ev.responseXML as Document | null | undefined;

			// If there is no responseXML, return false.
			// If none of the updateIds are in responseXML, return false.
			if (responseXML === undefined || responseXML === null) {
				return false;
			} else {
				if (Array.isArray(listenerOptions["updateId"])) {
					const updateIds = listenerOptions["updateId"] as string[];
					for (let i = 0; i < updateIds.length; i++) {
						const updateId = updateIds[i];
						const updateElement = responseXML.querySelector(`update[id="${updateId}"]`);
						if (updateElement !== null) {
							return true;
						}
					}
				} else {
					const updateId = listenerOptions["updateId"] as string;
					const updateElement = responseXML.querySelector(`update[id="${updateId}"]`);
					if (updateElement !== null) {
						return true;
					}
				}

				// If we got this far, there was no match so don't notify  this listener.
				return false;
			}
		}

		return true;
	}

	private initPrimeFacesAjaxEventListening() {
		if (this.isInitPrimeFacesAjaxEventListening) {
			return;
		}
		const pfAjax = this.getPrimeFacesAjax();
		if (pfAjax === undefined) {
			return;
		}

		// Note: Using functions and not arrow functions so `this` points to whatever context they are called in by PrimeFaces.
		const self = this;
		const originalPfResponseHandle = pfAjax.Request.handle; // This may be the docs: https://primefaces.github.io/primefaces/jsdocs/interfaces/primefaces.ajax.request.html#handle
		
		pfAjax.Request.handle = function(requestSettings: PrimeFacesJsfAjaxRequestSettings, paramsMaybe: any) {
			const originals = {
				onerror: requestSettings.onerror,
				onstart: requestSettings.onstart,
				onsuccess: requestSettings.onsuccess,
				oncomplete: requestSettings.oncomplete
			};

			// An object to pass around between request intercepts and all listeners. Used to communicate and
			// not provided to JSF and part of the actual AJAX request
			const internalOptions = Object.create(null);

			// onerror = error
			requestSettings.onerror = function(
				jqXHR: JQueryXHR,
				textStatus: null|JQuery.Ajax.ErrorTextStatus,
				errorThrown: string
			) {
				let returnValue;
				const msg = textStatus !== null ? textStatus + ": " + errorThrown : errorThrown;
				try {
					self.eventManager.notifyEventListeners("error", {
						eventType: "error",
						message: msg,
						internalOptions
					}, self.filterEachListenerForNotify);

					if (originals.onerror !== undefined) {
						returnValue = originals.onerror.call(this, jqXHR, textStatus, errorThrown);
					}
				} finally {
					self.eventManager.notifyEventListeners("always", {
						eventType: "always",
						message: msg,
						sourceElement: convertSourceToElement(self.win.document, requestSettings.source),
						internalOptions
					}, self.filterEachListenerForNotify);
				}

				return returnValue;
			};

			// onstart = begin
			requestSettings.onstart = function(startSettings: PrimeFacesJsfAjaxRequestSettings) {
				const sourceElement = typeof startSettings.source === "string"
					? self.win.document.getElementById(startSettings.source)
					: (startSettings.source as any).jquery !== undefined
					? (startSettings.source as any)?.get()[0]
					: startSettings.source;
				if (sourceElement === null || sourceElement === undefined) {
					throw new Error("Attempted to start an AJAX request without a source element");
				}
				let returnValue;
				self.eventManager.notifyEventListeners("begin", {
					eventType: "begin",
					sourceElement: sourceElement,
					internalOptions
				}, self.filterEachListenerForNotify);

				// Need to make the source available to the onsuccess and oncomplete handlers
				const currentOnSuccess = startSettings.onsuccess;
				startSettings.onsuccess = function(responseXML: Document, textStatus: string, jqXHR: JQueryXHR) {
					if (currentOnSuccess !== undefined) {
						(jqXHR as JQueryXhrEnhancedResponse).sourceElement = sourceElement;
						return currentOnSuccess.call(this, responseXML, textStatus, jqXHR);
					}
				};

				const currentOnComplete = startSettings.oncomplete;
				startSettings.oncomplete = function(jqXHR: JQueryXHR, textStatus: JQuery.Ajax.TextStatus, pfArgs: any) {
					if (currentOnComplete !== undefined) {
						(jqXHR as JQueryXhrEnhancedResponse).sourceElement = sourceElement;
						return currentOnComplete.call(this, jqXHR, textStatus, pfArgs);
					}
				};

				// Call original
				if (originals.onstart !== undefined) {
					returnValue = originals.onstart.call(this, startSettings);
				}

				return returnValue;
			};

			// onsuccess = responded
			requestSettings.onsuccess = function(
				responseXML: Document, // This is the same as jqXHR.responseXML
				textStatus: "success"|string, // Not sure. My guess is just "success", but jQuery is not clear: https://api.jquery.com/jQuery.ajax/
				jqXHR: JQueryXHR
			) {
				let returnValue;
				const newEv: JsfAjaxResponseEvent = {
					eventType: "responded",
					sourceElement: (jqXHR as JQueryXhrEnhancedResponse).sourceElement,
					responseCode: jqXHR.status,
					responseText: jqXHR.responseText,
					responseXML: jqXHR.responseXML,
					internalOptions
				};
				self.eventManager.notifyEventListeners("responded", newEv, self.filterEachListenerForNotify);

				if (originals.onsuccess !== undefined) {
					returnValue = originals.onsuccess.call(this, responseXML, textStatus, jqXHR);
				}

				return returnValue;
			};

			// oncomplete = success
			requestSettings.oncomplete = function(
				jqXHR: JQueryXHR,
				textStatus: JQuery.Ajax.TextStatus,
				pfArgs: any
			) {
				let returnValue;
				const newEv: JsfAjaxResponseEvent = {
					eventType: "success",
					sourceElement: (jqXHR as JQueryXhrEnhancedResponse).sourceElement,
					responseCode: jqXHR.status,
					responseText: jqXHR.responseText,
					responseXML: jqXHR.responseXML,
					internalOptions
				};
				self.eventManager.notifyEventListeners("success", newEv, self.filterEachListenerForNotify);

				if (originals.oncomplete !== undefined) {
					returnValue = originals.oncomplete.call(this, jqXHR, textStatus, pfArgs);
				}

				const alwaysEv: JsfAjaxAlwaysAfterSuccessEvent = {
					eventType: "always",
					sourceElement: (jqXHR as JQueryXhrEnhancedResponse).sourceElement,
					responseCode: jqXHR.status,
					responseText: jqXHR.responseText,
					responseXML: jqXHR.responseXML,
					internalOptions
				};
				self.eventManager.notifyEventListeners("always", alwaysEv, self.filterEachListenerForNotify);

				return returnValue;
			};

			return originalPfResponseHandle.call(this, requestSettings, paramsMaybe);
		};
		
		this.isInitPrimeFacesAjaxEventListening = true;
	}


	/**
	 * This method provides a way to listen for and intercept jsf.ajax.request() calls.
	 * The listeners are called immediately before jsf.ajax.request() does any work.
	 * This allows us to do a few things like  modify the request (e.g. injecting request parameters using JavaScript).
	 *
	 * The second parameter of this method allows you to limit your listening
	 * to a specific source element (e.g. to the anchor which is the source element of a "h:commandLink").
	 * The non-element specific (aka "global") listeners are called after any element specific element listeners.
	 *
	 * Note: Consider using `addEventListener()` from this class instead of this method. Only use this if you need to
	 * modify the request.
	 */
	public addNativeJsfAjaxRequestListener(listener: ListenerFunction, sourceElem?: Element) {
		this.initNativeJsfAjaxRequestIntercept();

		if (sourceElem === undefined) {
			if (this.onRequestListenersAll === undefined) {
				this.onRequestListenersAll = new Set<ListenerFunction>();
			}
			this.onRequestListenersAll.add(listener);
		} else {
			if (this.onRequestListenersForSpecificElements === undefined) {
				this.onRequestListenersForSpecificElements = new WeakMap<Element, Set<ListenerFunction>>();
			}
			const onRequestListenersForSourceElem = this.onRequestListenersForSpecificElements.get(sourceElem);
			if (onRequestListenersForSourceElem !== undefined) {
				onRequestListenersForSourceElem.add(listener);
			} else {
				const listeners = new Set<ListenerFunction>();
				listeners.add(listener);
				this.onRequestListenersForSpecificElements.set(sourceElem, listeners);
			}
		}
	}

	public removeNativeJsfAjaxRequestListener(listener: ListenerFunction, sourceElem?: Element): boolean {
		if (sourceElem === undefined) {
			return this.onRequestListenersAll !== undefined && this.onRequestListenersAll.delete(listener);
		} else if (this.onRequestListenersForSpecificElements !== undefined) {
			const onRequestListenersForSourceElem = this.onRequestListenersForSpecificElements.get(sourceElem);
			return onRequestListenersForSourceElem !== undefined && onRequestListenersForSourceElem.delete(listener);
		}
		return false;
	}

	private initNativeJsfAjaxRequestIntercept() {
		if (this.isInitAjaxRequestInterceptSetup) {
			return;
		}
		const jsfAjax = this.getNativeJsfAjax();
		if (jsfAjax === undefined) {
			return;
		}
		
		const originalRequestElement = jsfAjax.request;
		jsfAjax.request = (source: string | HTMLElement, event: any, requestOptions?: NativeJsfAjaxRequestOptions) => {
			if (requestOptions === undefined) {
				// If there are no options, create an empty object so request intercept listeners can modify it.
				// Note: Do not use Object.create(null) because JSF needs the methods on a standard object.
				requestOptions = {};
			}

			const _this = this;

			// An object which will be a readonly copy of the request information. Passed to listeners so they can use
			// the information to make decisions.
			// The value is set after request intercept listeners are called which is before the AJAX request is
			// made but before the other listeners are called..
			let requestOptionsReadonlyCopy: Readonly<NativeJsfAjaxRequestOptions>;

			// An object to pass around between request intercepts and all listeners. Used to communicate and
			// not provided to JSF and part of the actual AJAX request
			const internalOptions = Object.create(null);

			// sourceElement
			const sourceElement = typeof source === "string" ? this.win.document.getElementById(source) : source;

			// setup onevent and onerror intercept
			// (not using addOnError and addOnEvent because I want more info)
			const onMethodNames = ["onevent", "onerror"];
			const onMethodListenerNotifier = [_this.onNativeJsfOnEvent, _this.onNativeJsfOnError];
			for (let i=0; i < onMethodNames.length; i++) {
				let methodName = onMethodNames[i]!;
				let notifier = onMethodListenerNotifier[i]!;
				const originalOn = requestOptions[methodName];
				requestOptions[methodName] = function(ev: NativeJsfAjaxEvent) {
					notifier.call(_this, ev, requestOptionsReadonlyCopy, internalOptions);
					if (originalOn !== undefined) {
						originalOn.call(this, ev);
					}
				};
			}

			// Call request intercept listeners
			if (sourceElement instanceof Element) {
				// Call element specific listeners
				if (this.onRequestListenersForSpecificElements !== undefined) {
					const listenersForElem = this.onRequestListenersForSpecificElements.get(sourceElement);
					if (listenersForElem !== undefined) {
						listenersForElem.forEach((listener: ListenerFunction) => {
							listener.call(
								null,
								sourceElement,
								event,
								requestOptions!,
								internalOptions
							);
						});
					}
				}
				
				// Call global listeners
				if (this.onRequestListenersAll !== undefined && this.onRequestListenersAll.size > 0) {
					this.onRequestListenersAll.forEach((listener: ListenerFunction) => {
						listener.call(
							null,
							sourceElement,
							event,
							requestOptions!,
							internalOptions
						);
					});
				}
			}

			// JSF's JS expects the objects in options to have a prototype of Object so it has methods like
			// hasOwnProperty(), so we need to make sure the options do not contain any object with
			// a 'null' constructor.
			const objProto = Object.getPrototypeOf({});
			deepForEach(
				requestOptions,
				(value) => {
					if (
						value !== undefined && value !== null
						&& Object.getPrototypeOf(value) === null
					) {
						value = Object.setPrototypeOf(value, objProto);
					}
					return value;
				}
			);

			// Now that everything has run, create a readonly copy for listeners to have access to
			const deepCopyOfRequestOptions = deepCopyObject(requestOptions);
			if (deepCopyOfRequestOptions.originalSourceId === undefined) {
				deepCopyOfRequestOptions.originalSourceId = sourceElement?.id ?? undefined;
			}
			requestOptionsReadonlyCopy = deepFreeze(deepCopyOfRequestOptions);

			// Call the original jsf.ajax.request() method.
			originalRequestElement(source, event, requestOptions);
		}

		this.isInitAjaxRequestInterceptSetup = true;
	}

	private onNativeJsfOnError = (ev: NativeJsfAjaxEvent, requestOptionsReadonlyCopy: NativeJsfAjaxRequestOptions, internalOptions: any) => {
		try {
			this.eventManager.notifyEventListeners("error", {
				eventType: "error",
				message: ev.description,
				requestOptionsReadonlyCopy,
				internalOptions
			}, this.filterEachListenerForNotify);
		} finally {
			const resolvedOriginalSource = requestOptionsReadonlyCopy.originalSourceId !== undefined
				? document.getElementById(requestOptionsReadonlyCopy.originalSourceId) ?? undefined : undefined;
			this.eventManager.notifyEventListeners("always", {
				eventType: "always",
				message: ev.description,
				sourceElement: resolvedOriginalSource,
				requestOptionsReadonlyCopy,
				internalOptions
			}, this.filterEachListenerForNotify);
		}
	}

	private onNativeJsfOnEvent = (ev: NativeJsfAjaxEvent, requestOptionsReadonlyCopy: NativeJsfAjaxRequestOptions, internalOptions: any) => {
		if (ev.status === NativeJsfAjaxEventStatus.Begin) {
			this.eventManager.notifyEventListeners("begin", {
				eventType: "begin",
				sourceElement: ev.source,
				requestOptionsReadonlyCopy,
				internalOptions
			}, this.filterEachListenerForNotify);
		} else if (ev.status === NativeJsfAjaxEventStatus.Complete) {
			// ev.responseCode, ev.responseText, and ev.responseXML are always defined for complete and success events
			this.eventManager.notifyEventListeners("responded", {
				eventType: "responded",
				sourceElement: ev.source,
				responseCode: ev.responseCode!,
				responseText: ev.responseText!,
				responseXML: ev.responseXML!,
				requestOptionsReadonlyCopy,
				internalOptions
			}, this.filterEachListenerForNotify);
		} else if (ev.status === NativeJsfAjaxEventStatus.Success) {
			const resolvedSource = ev.source ?? (requestOptionsReadonlyCopy.originalSourceId !== undefined
				? document.getElementById(requestOptionsReadonlyCopy.originalSourceId) ?? undefined : undefined);
			try {
				this.eventManager.notifyEventListeners("success", {
					eventType: "success",
					sourceElement: resolvedSource,
					responseCode: ev.responseCode!,
					responseText: ev.responseText!,
					responseXML: ev.responseXML!,
					requestOptionsReadonlyCopy,
					internalOptions
				}, this.filterEachListenerForNotify);
			} finally {
				this.eventManager.notifyEventListeners("always", {
					eventType: "always",
					sourceElement: resolvedSource,
					responseCode: ev.responseCode,
					responseText: ev.responseText,
					responseXML: ev.responseXML,
					requestOptionsReadonlyCopy,
					internalOptions
				}, this.filterEachListenerForNotify);
			}
		}
	}


	public isNativeJsfAjaxLoaded(): boolean {
		return this.getNativeJsfAjax() !== undefined;
	}

	/**
	 * Returns window.jsf.ajax or undefined.
	 * Note: Yes, I know it is not "native". I just needed to distinguish it somehow.
	 */
	private getNativeJsfAjax(): NativeJsfAjax | undefined {
		return this.win.jsf?.ajax;
	}

	/**
	 * Returns window.PrimeFaces.ajax or undefined.
	 */
	private getPrimeFacesAjax(): PrimeFacesJsfAjax | undefined {
		return this.win.PrimeFaces?.ajax;
	}

	/**
	 * This is used by EventListenerManager
	 */
	getEventListenerManagerData(): AddRemoveData<JsfAjax> {
		const isDestroyed = (instance: JsfAjax) =>
			JsfAjax.instances === undefined || !JsfAjax.instances.has(instance.win);
		return {
			"addNativeJsfAjaxRequestListener": {
				removeMethodName: "removeNativeJsfAjaxRequestListener",
				shouldCleanup: isDestroyed
			},
			"addEventListener": {
				removeMethodName: "removeEventListener",
				shouldCleanup: isDestroyed
			}
		};
	}
}




// Documentation on jsf.ajax: https://javaserverfaces.github.io/docs/2.3/jsdocs/symbols/jsf.ajax.html
interface NativeJsfAjax {
	addOnError:	(cb: (ev: NativeJsfAjaxEvent) => any) => void,
	addOnEvent:	(cb: (ev: NativeJsfAjaxEvent) => any) => void
	request:	(source: string|HTMLElement, event: any, options?: NativeJsfAjaxRequestOptions) => void,
	response: 	(request: XMLHttpRequest, context: {
		element: HTMLElement,
		form: HTMLFormElement,
		formData: FormData,
		formId: string,
		includesInputFile: boolean,
		namingContainerId: string,
		namingContainerPrefix: string,
		onerror: any,
		onevent: any,
		render: string,
		sourceid: string // This is turned into the "source" property on NativeJsfAjaxEvent
	}) => void
}

export interface NativeJsfAjaxEvent {
	type: string,
	status: NativeJsfAjaxEventStatus,
	source: HTMLElement,
	statusScope: string,
	statusScopeExplicitlySet: boolean,
	applicableStatuses: string[],
	fireType: string,

	// Responses also have
	responseCode?: number,
	responseXML?: Document,
	responseText?: string,
	hasError?: boolean,

	// For error type
	description?: string
}

export enum NativeJsfAjaxEventStatus {
	// Before AJAX request begins
	Begin       = "begin",
	// Called upon receiving AJAX response but before making updates.
	Complete    = "complete",
	// After updates are made.
	Success     = "success"
}

export interface NativeJsfAjaxRequestParams {
	[key: string]: string|number|boolean;
}

// Declare an interface and a listener function signature
export interface NativeJsfAjaxRequestOptions {
	// Some documentation is here: https://docs.oracle.com/cd/E17802_01/j2ee/javaee/javaserverfaces/2.0/docs/js-api/symbols/jsf.ajax.html#.request
	// While the following is all that is defined, more may be added.
	"javax.faces.behavior.event"? : string;
	// Space separated list of client identifiers. But I have seen the use of 0 for `false` in some JS code to disable it. If not used, it typically should be undefined not 0 though.
	execute?: string|0;
	// Space separated list of client identifiers
	render?: string;
	// A function to callback for event
	onevent?: (ev: NativeJsfAjaxEvent) => void;
	// A function to callback for error
	onerror?: (ev: NativeJsfAjaxEvent) => void;
	// object containing parameters to include in the request
	params?: NativeJsfAjaxRequestParams;
	originalSourceId?: string;
	[key: string]: any;
}


interface PrimeFacesJsfAjax {
	Request: {
		handle: (
			requestSettings: PrimeFacesJsfAjaxRequestSettings,
			paramsMaybe: any // Not sure what this is. May be used for multiple things.
		) => void
		// ... There is more.
	},
	Response: {
		handle: (
			// This is called upon the jQuery.ajax's "success"
			// This appears to be the "success" signature of jQuery.ajax(): https://api.jquery.com/jQuery.ajax/
			responseXML: Document, // This is the same as jqXHR.responseXML
			textStatus: null|JQuery.Ajax.TextStatus,
			jqXHR: JQueryXHR,
			b: any
		) => void
		// ... There is more.
	}
	// ... There is more.
}
declare type PrimeFacesJsfAjaxOnComplete = (
	jqXHR: JQueryXHR,
	textStatus: JQuery.Ajax.TextStatus,
	pfArgs: any
) => any;

// Docs: https://primefaces.github.io/primefaces/jsdocs/interfaces/primefaces.ajax.configuration.html
// I have not declared all properties here
declare type PrimeFacesJsfAjaxRequestSettings = {
	event: string,
	source: string | JQuery | HTMLElement,
	process: string,
	onstart?: (requestSettings: PrimeFacesJsfAjaxRequestSettings) => any,
	onerror?: (
		jqXHR: JQueryXHR,
		textStatus: null|JQuery.Ajax.ErrorTextStatus,
		errorThrown: string
	) => any,
	onsuccess?: (
		responseXML: Document, // This is the same as jqXHR.responseXML
		textStatus: "success"|string, // Not sure. My guess is just "success", but jQuery is not clear: https://api.jquery.com/jQuery.ajax/
		jqXHR: JQueryXHR
	) => any,
	oncomplete?: PrimeFacesJsfAjaxOnComplete,
	ext?: any,
	global: boolean,
	params: {name: string, value: string}[] // Note: This is fairly different from "native" JSF's params
};

interface JQueryXhrEnhancedResponse extends JQueryXHR {
	sourceElement: HTMLElement
}

declare global {
	interface Window {
		jsf?: { ajax?: NativeJsfAjax };
		PrimeFaces?: { ajax?: PrimeFacesJsfAjax };
	}
}
